import React, { useState } from 'react';
import './App.css';
import { ACTIVE_BRAND } from './branding';
import HomePage from './pages/HomePage';
import ReadyToStartPage from './pages/ReadyToStartPage';
import ScanningPage from './pages/ScanningPage';
import SelfiePage from './pages/SelfiePage';
import ProcessingPage from './pages/ProcessingPage';
import ResultsPage from './pages/ResultsPage';
import TopUpScreen from './pages/TopUpScreen';
import ConfigModal from './components/ConfigModal';
import { QRCodeSVG } from 'qrcode.react';

const API_ENDPOINTS = {
  createApplication: 'https://2qn6jp7ava.execute-api.ap-southeast-2.amazonaws.com/Prod/create-application',
  captureRequestFront: 'https://2qn6jp7ava.execute-api.ap-southeast-2.amazonaws.com/Prod/capture-request-front',
  captureRequestBack: 'https://2qn6jp7ava.execute-api.ap-southeast-2.amazonaws.com/Prod/capture-request-back',
  triggerScan: 'https://2qn6jp7ava.execute-api.ap-southeast-2.amazonaws.com/Prod/trigger-scan',
  executeWorkflow: 'https://2qn6jp7ava.execute-api.ap-southeast-2.amazonaws.com/Prod/execute-workflow',
  checkStatus: 'https://2qn6jp7ava.execute-api.ap-southeast-2.amazonaws.com/Prod/check-status',
  execute: 'https://2qn6jp7ava.execute-api.ap-southeast-2.amazonaws.com/Prod/execute',
  getResults: 'https://2qn6jp7ava.execute-api.ap-southeast-2.amazonaws.com/Prod/get-results',
  getElementFile: 'https://2qn6jp7ava.execute-api.ap-southeast-2.amazonaws.com/Prod/get-element-file',
  uploadToS3: 'https://2qn6jp7ava.execute-api.ap-southeast-2.amazonaws.com/Prod/upload-to-s3',
  compareFaces: 'https://2qn6jp7ava.execute-api.ap-southeast-2.amazonaws.com/Prod/compare-faces',
  cancelScan: 'https://2qn6jp7ava.execute-api.ap-southeast-2.amazonaws.com/Prod/cancel-scan',
  createDigitalCard: 'https://2qn6jp7ava.execute-api.ap-southeast-2.amazonaws.com/Prod/create-digital-card'
};

function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [selectedService, setSelectedService] = useState('');
  const [applicationId, setApplicationId] = useState('');
  const [requestId, setRequestId] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [selfieImage, setSelfieImage] = useState(null);
  const [faceComparisonResult, setFaceComparisonResult] = useState(null);
  const [documentImages, setDocumentImages] = useState({ dlf: null, dlb: null });
  const [digitalCard, setDigitalCard] = useState(null);
  const [showConfig, setShowConfig] = useState(false);

  // Configuration state
  const [applicationTitle, setApplicationTitle] = useState('max gaming demo');
  const [serialNumber, setSerialNumber] = useState(ACTIVE_BRAND.serialNumber || '54A20360001');
  const [timeout, setTimeout] = useState(15);

  const handleServiceSelect = (serviceId) => {
    setSelectedService(serviceId);
    if (serviceId === 'membership-management' && ACTIVE_BRAND.name === 'MAX Gaming') {
      setCurrentPage('topUp');
    } else {
      setCurrentPage('ready');
    }
  };

  const handleStartScan = () => {
    setCurrentPage('scanning');
  };

  const handleCancelScan = () => {
    if (requestId) {
      fetch(API_ENDPOINTS.cancelScan, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ request_id: requestId })
      }).catch(err => {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Cancel failed:', err);
        }
      });
    }

    // Reset and go home
    setApplicationId('');
    setRequestId('');
    setScanResult(null);
    setCurrentPage('home');
  };

  const handleSelfieReady = () => {
    setCurrentPage('selfie');
  };

  const handleScanComplete = (classification) => {
    setScanResult(classification);
    // If we already have selfie and are on processing page, go to results
    if (selfieImage) {
      setCurrentPage('results');
    }
  };

  const handleSelfieComplete = async (selfieData) => {
    setSelfieImage(selfieData);
    setCurrentPage('processing');

    // Now execute and get results in background
    if (applicationId) {
      await executeAndGetResults(applicationId, selfieData);
    }
  };

  const executeAndGetResults = async (appId, selfieData) => {
    try {
      // First execute with Force_ReExecute
      const exec1Response = await fetch(API_ENDPOINTS.execute, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          application_id: appId,
          force_reexecute: true
        })
      });
      await exec1Response.json();

      // Wait a bit before second execute
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Second execute without Force_ReExecute
      const exec2Response = await fetch(API_ENDPOINTS.execute, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          application_id: appId,
          force_reexecute: false
        })
      });
      await exec2Response.json();

      // Wait a bit before getting results
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Get results
      const resultsResponse = await fetch(API_ENDPOINTS.getResults, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          application_id: appId
        })
      });
      const resultsData = await resultsResponse.json();

      // Handle nested response structure
      const result = resultsData.data?.Result || resultsData.Result;

      if (result && result.Elements && result.Elements.List) {
        // Find the element with Classification (usually DLF)
        let classification = null;

        for (let key in result.Elements.List) {
          const element = result.Elements.List[key];
          if (element.Classification) {
            classification = element.Classification;
            break;
          }
        }

        if (classification) {
          setScanResult(classification);

          // Create digital card as soon as classification is ready
          createDigitalCard(classification);

          // Fetch DLF and DLB images for face comparison
          await fetchAndCompareFaces(appId, selfieData);
        } else {
          if (process.env.NODE_ENV !== 'production') {
            console.error('No classification found');
          }
          setCurrentPage('results');
        }
      } else {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Invalid response structure');
        }
        setCurrentPage('results');
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error getting results:', error);
      }
      setCurrentPage('results');
    }
  };

  const fetchAndCompareFaces = async (appId, selfieImg) => {
    try {
      // Fetch DLF image
      const dlfResponse = await fetch(API_ENDPOINTS.getElementFile, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          application_id: appId,
          element_key: 'DLF',
          processed_file: false
        })
      });

      const dlfData = await dlfResponse.json();
      const dlfImage = dlfData.success ? `data:image/${dlfData.image_format};base64,${dlfData.image_data}` : null;

      // Fetch DLB image
      const dlbResponse = await fetch(API_ENDPOINTS.getElementFile, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          application_id: appId,
          element_key: 'DLB',
          processed_file: false
        })
      });

      const dlbData = await dlbResponse.json();
      const dlbImage = dlbData.success ? `data:image/${dlbData.image_format};base64,${dlbData.image_data}` : null;

      // Store DLF and DLB images for display
      setDocumentImages({ dlf: dlfImage, dlb: dlbImage });

      // Step 1: Get presigned URLs from Lambda
      if (process.env.NODE_ENV !== 'production') {
        console.log('Getting presigned S3 URLs...');
      }
      const urlResponse = await fetch(API_ENDPOINTS.uploadToS3, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          application_id: appId
        })
      });

      const urlData = await urlResponse.json();
      if (process.env.NODE_ENV !== 'production') {
        console.log('Presigned URLs received:', { success: urlData.success, hasUrls: !!urlData.urls });
      }

      if (!urlData.success || !urlData.urls) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Failed to get presigned URLs:', urlData);
        }
        setCurrentPage('results');
        return;
      }

      // Step 2: Upload images directly to S3 using presigned URLs
      const uploadPromises = [];

      if (selfieImg && urlData.urls.selfie) {
        // Convert data URL to blob
        const selfieBlob = await fetch(selfieImg).then(r => r.blob());
        uploadPromises.push(
          fetch(urlData.urls.selfie, {
            method: 'PUT',
            body: selfieBlob,
            headers: { 'Content-Type': 'image/jpeg' }
          }).then(() => ({ key: 'selfie', success: true }))
            .catch(err => ({ key: 'selfie', success: false, error: err }))
        );
      }

      if (dlfImage && urlData.urls.dlf) {
        const dlfBlob = await fetch(dlfImage).then(r => r.blob());
        uploadPromises.push(
          fetch(urlData.urls.dlf, {
            method: 'PUT',
            body: dlfBlob,
            headers: { 'Content-Type': 'image/jpeg' }
          }).then(() => ({ key: 'dlf', success: true }))
            .catch(err => ({ key: 'dlf', success: false, error: err }))
        );
      }

      if (dlbImage && urlData.urls.dlb) {
        const dlbBlob = await fetch(dlbImage).then(r => r.blob());
        uploadPromises.push(
          fetch(urlData.urls.dlb, {
            method: 'PUT',
            body: dlbBlob,
            headers: { 'Content-Type': 'image/jpeg' }
          }).then(() => ({ key: 'dlb', success: true }))
            .catch(err => ({ key: 'dlb', success: false, error: err }))
        );
      }

      const uploadResults = await Promise.all(uploadPromises);
      if (process.env.NODE_ENV !== 'production') {
        console.log('S3 upload results:', uploadResults);
      }

      const allUploaded = uploadResults.every(r => r.success);

      // Step 3: Compare faces using images from S3
      if (allUploaded) {
        if (process.env.NODE_ENV !== 'production') {
          console.log('All images uploaded to S3, comparing faces...');
        }
        const compareResponse = await fetch(API_ENDPOINTS.compareFaces, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            application_id: appId
          })
        });

        const compareData = await compareResponse.json();
        console.log('Face comparison result:', compareData);
        setFaceComparisonResult(compareData);
      } else {
        console.log('Some S3 uploads failed:', uploadResults.filter(r => !r.success));
      }

      // Navigate to results
      setCurrentPage('results');
    } catch (error) {
      console.error('Error in face comparison:', error);
      setCurrentPage('results');
    }
  };

  const handleBackToHome = () => {
    setApplicationId('');
    setRequestId('');
    setScanResult(null);
    setSelfieImage(null);
    setFaceComparisonResult(null);
    setDocumentImages({ dlf: null, dlb: null });
    setDigitalCard(null);
    setSelectedService('');
    setCurrentPage('home');
    setCurrentPage('home');
  };

  const handleTopUp = () => {
    setCurrentPage('topUp');
  };

  const createDigitalCard = async (classification) => {
    if (!ACTIVE_BRAND.digitalCard || !classification) return;

    try {
      const cardPayload = ACTIVE_BRAND.digitalCard.getPayload(classification);

      const requestBody = {
        templateId: ACTIVE_BRAND.digitalCard.templateId,
        cardPayload: cardPayload,
        options: {
          downloadId: null,
          emailInvitationEnabled: true,
          smsInvitationEnabled: false
        }
      };

      const response = await fetch(API_ENDPOINTS.createDigitalCard, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.success || data.demo) {
        setDigitalCard(data);
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error creating digital card:', error);
      }
    }
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <HomePage onServiceSelect={handleServiceSelect} />;
      case 'ready':
        return (
          <ReadyToStartPage
            service={selectedService}
            onStart={handleStartScan}
            onCancel={handleCancelScan}
          />
        );
      case 'scanning':
        return (
          <ScanningPage
            apiEndpoints={API_ENDPOINTS}
            applicationTitle={applicationTitle}
            serialNumber={serialNumber}
            timeout={timeout}
            onComplete={handleScanComplete}
            onCancel={handleCancelScan}
            onSelfieReady={handleSelfieReady}
            setApplicationId={setApplicationId}
            setRequestId={setRequestId}
          />
        );
      case 'selfie':
        return (
          <SelfiePage
            onComplete={handleSelfieComplete}
            onCancel={handleCancelScan}
          />
        );
      case 'processing':
        return <ProcessingPage />;
      case 'results':
        return (
          <ResultsPage
            classification={scanResult}
            apiEndpoints={API_ENDPOINTS}
            applicationId={applicationId}
            selfieImage={selfieImage}
            faceComparisonResult={faceComparisonResult}
            documentImages={documentImages}
            digitalCard={digitalCard}
            onBackToHome={handleBackToHome}
            onTopUp={handleTopUp}
          />
        );
      case 'topUp':
        return (
          <TopUpScreen
            onBackToHome={handleBackToHome}
          />
        );
      default:
        return <HomePage onServiceSelect={handleServiceSelect} />;
    }
  };

  return (
    <div className="App" style={{
      background: '#f5f5f5',
      overflow: currentPage === 'results' ? 'auto' : 'hidden'
    }}>
      <header className="App-header" style={{ background: ACTIVE_BRAND.colors.primary }}>
        <img
          src={ACTIVE_BRAND.logo}
          alt={ACTIVE_BRAND.name}
          className="brand-logo"
          style={ACTIVE_BRAND.name === 'IDV Pacific' ? {
            background: '#FFFFFF',
            padding: '0.5rem',
            borderRadius: '8px'
          } : {}}
        />
        <button
          className="config-button"
          onClick={() => setShowConfig(true)}
          title="Configuration"
        >
          ⚙️
        </button>
      </header>

      {ACTIVE_BRAND.qrCode && (
        <div className="qr-code-section" style={{
          position: 'fixed',
          right: '1.5rem',
          top: '200px',
          zIndex: 99,
          background: ACTIVE_BRAND.colors.primary
        }}>
          <div className="qr-code-text">Scan to Join</div>
          <QRCodeSVG
            value={ACTIVE_BRAND.qrCode.url}
            size={140}
            bgColor={ACTIVE_BRAND.qrCode.bgColor}
            fgColor={ACTIVE_BRAND.qrCode.fgColor}
            level="M"
          />
        </div>
      )}

      {renderPage()}

      {showConfig && (
        <ConfigModal
          applicationTitle={applicationTitle}
          setApplicationTitle={setApplicationTitle}
          serialNumber={serialNumber}
          setSerialNumber={setSerialNumber}
          timeout={timeout}
          setTimeout={setTimeout}
          onClose={() => setShowConfig(false)}
        />
      )}
    </div>
  );
}

export default App;
